import styled from 'styled-components';
import { Slider } from 'antd';
import { SIZE_SPAN } from './useCountModel';

/**
 * The count panel's size filter (Find's size filter for a video, FindResults.SizeRow): every track's size as bars on
 * a log scale, and the range kept. In metres on the ground when the video has flight data, starting at the usual
 * size of what is counted ÷ and × SIZE_SPAN; in pixels otherwise, starting with every size.
 */

const tk = ({ theme: t }) => t.token;

const BINS = 28;

const Box = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXXS}px;
`;

const Line = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: ${p => tk(p).marginXS}px;
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextSecondary};
	b {
		color: ${p => tk(p).colorTextLightSolid};
		font-weight: ${p => tk(p).fontWeightStrong};
	}
`;

const Bars = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${p => tk(p).lineWidth}px;
	height: ${p => tk(p).sizeLG}px;
	margin: 0 ${p => tk(p).sizeXS}px;
`;

const Bar = styled.div`
	flex: 1;
	min-height: ${p => tk(p).lineWidth}px;
	height: ${p => p.$height * 100}%;
	border-radius: ${p => tk(p).lineWidth}px;
	background: ${p => (p.$in ? p.$color || tk(p).colorPrimary : tk(p).colorFillSecondary)};
`;

const Dot = styled.i`
	display: inline-block;
	width: ${p => tk(p).sizeXS}px;
	height: ${p => tk(p).sizeXS}px;
	margin-right: ${p => tk(p).marginXXS}px;
	border-radius: 50%;
`;

const Small = styled.div`
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextTertiary};
	line-height: ${p => tk(p).lineHeightSM};
`;

const Link = styled.button`
	border: 0;
	padding: 0;
	background: transparent;
	color: ${p => tk(p).colorBrandLight};
	font-size: ${p => tk(p).fontSizeXS}px;
	text-decoration: underline;
	cursor: pointer;
`;

const fmt = (v, unit) =>
	unit === 'm'
		? `${v < 1 ? v.toFixed(2) : v < 10 ? v.toFixed(1) : Math.round(v)} m`
		: `${Math.round(v)} px`;

// one kind's row: its tracks' sizes as bars, the range kept, the slider
const SizeRow = ({
	unit,
	values,
	range,
	auto,
	usual,
	hidden,
	name,
	color,
	several,
	onChange,
	onReset,
}) => {
	if (!values?.length) return null;
	const lo =
		Math.max(unit === 'm' ? 0.05 : 1, Math.min(...values, range ? range[0] : Infinity)) / 1.25;
	const hi = Math.max(...values, range ? range[1] : 0) * 1.25;
	const span = Math.log(hi / lo) || 1;
	const toPosition = v => Math.round((Math.log(Math.max(v, lo) / lo) / span) * 1000);
	const toValue = position => lo * Math.exp((position / 1000) * span);
	const [min, max] = range ?? [lo, hi];
	const bins = new Array(BINS).fill(0);
	values.forEach(v => {
		const k = Math.floor((toPosition(v) / 1000) * BINS);
		bins[Math.min(BINS - 1, Math.max(0, k))] += 1;
	});
	const tallest = Math.max(...bins, 1);
	const inRange = k => {
		const v = toValue(((k + 0.5) / BINS) * 1000);
		return v >= min && v <= max;
	};
	return (
		<Box>
			<Line>
				<span>
					{several && <Dot style={{ background: color }} />}
					{several ? `${name} · keep ` : 'Keep '}
					<b>{range ? `${fmt(min, unit)} – ${fmt(max, unit)}` : 'every size'}</b>
					{hidden ? ` · ${hidden} hidden` : ''}
					{several && unit === 'm' && usual?.length
						? ` · usual ${fmt(usual[0], 'm')}`
						: ''}
				</span>
				{!auto && (
					<Link type="button" onClick={onReset}>
						Reset
					</Link>
				)}
			</Line>
			<Bars aria-hidden>
				{bins.map((n, k) => (
					<Bar key={k} $height={n / tallest} $in={inRange(k)} $color={color} />
				))}
			</Bars>
			<Slider
				range
				min={0}
				max={1000}
				value={[toPosition(Math.max(min, lo)), toPosition(Math.min(max, hi))]}
				tooltip={{ formatter: position => fmt(toValue(position), unit) }}
				onChange={([from, to]) =>
					onChange(from <= 0 && to >= 1000 ? null : [toValue(from), toValue(to)])
				}
				aria-label={`Sizes of ${name} to keep`}
			/>
		</Box>
	);
};

// groups: one per kind counted ([{ kind, values, range, auto, usual, hidden }], kind '' for a one-kind video)
const SizeFilter = ({ unit, groups, subject, colors = null, onChange, onReset }) => {
	const rows = (groups || []).filter(g => g.values.length);
	if (!rows.length) return null;
	const several = rows.length > 1;
	const usual = rows.flatMap(g => g.usual || []);
	const usualText =
		unit === 'm' && usual.length
			? several
				? `Each kind starts at its usual size ÷ and × ${SIZE_SPAN}.`
				: `Starts at the usual size of ${subject} (${usual.map(u => fmt(u, 'm')).join(', ')}) ÷ and × ${SIZE_SPAN}.`
			: unit === 'm'
				? 'No usual size is known for this, so it starts with every size.'
				: 'In pixels: this video has no flight data to give metres, and something far away looks small.';
	return (
		<Box>
			{rows.map(g => (
				<SizeRow
					key={g.kind || 'all'}
					unit={unit}
					{...g}
					name={g.kind || subject}
					color={colors?.[g.kind]?.hex}
					several={several}
					onChange={r => onChange(g.kind, r)}
					onReset={() => onReset(g.kind)}
				/>
			))}
			<Small>{usualText}</Small>
		</Box>
	);
};

export default SizeFilter;
